import { useState, useEffect } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { LoginForm } from '../components/LoginForm'
import { ForgotPasswordForm } from '../components/ForgotPasswordForm'
import { ResetPasswordForm } from '../components/ResetPasswordForm'
import { SuperstateLogo } from '../components/SuperstateLogo'
import { Button } from '@/components/ui/button'

export function LoginPage() {
  const [currentView, setCurrentView] = useState<'login' | 'forgot-password'>('login')
  const [error, setError] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const { token, login, requestPasswordReset, loading } = useAuth()
  const navigate = useNavigate()

  const handleLogin = async (email: string, password: string, rememberMe?: boolean) => {
    try {
      setError('')
      await login(email, password, rememberMe)
      navigate('/dashboard')
    } catch (err: any) {
      const message = err instanceof Error ? err.message : 'Unable to sign in. Please try again.'
      setError(message)
    }
  }

  const handleForgotPassword = async (email: string) => {
    try {
      setError('')
      await requestPasswordReset(email)
      setShowSuccess(true)
    } catch (err: any) {
      const message = err instanceof Error ? err.message : 'Unable to send reset instructions. Please try again.'
      setError(message)
    }
  }

  const resetToLogin = () => {
    setCurrentView('login')
    setError('')
    setShowSuccess(false)
  }

  if (token) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div className="min-h-screen bg-login-gradient flex flex-col items-center justify-center py-12 px-6">
      <div className="mb-8">
        <SuperstateLogo className="h-[25px] w-[255px]" variant="#FFFFFF" />
      </div>

      {currentView === 'login' && (
        <LoginForm
          onLogin={handleLogin}
          onForgotPassword={() => setCurrentView('forgot-password')}
          loading={loading}
          error={error}
        />
      )}

      {currentView === 'forgot-password' && (
        <ForgotPasswordForm
          onRequestReset={handleForgotPassword}
          onBackToLogin={resetToLogin}
          loading={loading}
          error={error}
          success={showSuccess}
        />
      )}
    </div>
  )
}

export function PasswordResetPage() {
  const [searchParams] = useSearchParams()
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const { resetPassword, loading } = useAuth()
  const navigate = useNavigate()

  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token')
    }
  }, [token])

  const handleResetComplete = async (newPassword: string) => {
    if (!token) {
      setError('Invalid reset token')
      return
    }

    try {
      setError('')
      await resetPassword(token, newPassword)
      setSuccess(true)
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-main flex items-center justify-center py-12 px-6">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white shadow-lg rounded-lg p-8 text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Invalid Reset Link</h2>
            <p className="text-gray-600 mb-6">This password reset link is invalid or has expired.</p>
            <Button onClick={handleBackToLogin}>Back to Sign In</Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-main flex items-center justify-center py-12 px-6">
      <ResetPasswordForm
        token={token}
        onResetComplete={handleResetComplete}
        onBackToLogin={handleBackToLogin}
        loading={loading}
        error={error}
        success={success}
      />
    </div>
  )
}
